import xlsx, { ISettings, IJsonSheet, IColumn, IContent } from "json-as-xlsx";
import moment from "moment";
import {
  RelatorioInadimplentesResponse,
  ContasRelatorioInadimplentes,
  RelatorioInadimplentesItem,
} from "features/relatorios/typings";
import { formatMoney } from "infra/helpers/number";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";
import { sanitizeSheetName } from "infra/helpers/sanitizeSheetName";
import { mapItemsTotalWithSelectedFields } from "./mapItemsTotalWithSelectedFields";
import {
  RelatorioInadimplentesFields,
  ResumoCategoriaItem,
} from "core/features/relatorios/typings";

export function exportExcelRelatorioInadimplentes({
  data,
  selectedFields,
}: {
  data: RelatorioInadimplentesResponse;
  selectedFields?: RelatorioInadimplentesFields[];
}) {
  const settings: ISettings = {
    fileName: `Relatório de Inadimplentes - ${moment().format("DD_MM_YYYY")}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columnsInadimplentes = getColumnsInadimplentes(selectedFields);

  const columnsTotais = getColumnsTotais(selectedFields);

  const columns = getColumns(selectedFields);

  const sheets: IJsonSheet[] = [];

  const { items: itemsWithMappedTotal, total: mappedTotal } =
    mapItemsTotalWithSelectedFields(data?.items ?? [], selectedFields ?? []);

  sheets.push({
    sheet: `Inadimplentes (${formatMoney(mappedTotal)})`.slice(0, 31),
    columns: columnsInadimplentes,
    content: itemsWithMappedTotal.map(defaultContentMapperXlsx),
  });

  if (
    selectedFields?.includes("resumoContaCategoria") &&
    data.resumoPorCategoria
  ) {
    const columnsResumo = getColumnsResumo();

    sheets.push({
      sheet: "Resumo por categoria",
      columns: columnsResumo,
      content: data.resumoPorCategoria.map(defaultContentMapperXlsx),
    });
  }

  sheets.push({
    sheet: "Totais",
    columns: columnsTotais,
    content: [
      {
        numeroInadimplentes: data.numeroInadimplentes,
        totalAcordos: data.totalAcordos,
        totalUnidades: data.totalUnidades,
        total: mappedTotal,
        totalMulta: data.totalMulta,
        totalJuros: data.totalJuros,
        totalTaxaCobranca: data.totalTaxaCobranca,
        totalCorrecaoMonetaria: data.totalCorrecaoMonetaria,
        totalPrincipal: data.totalPrincipal,
        dataPosicao: data.dataPosicao
          ? moment(data.dataPosicao).format("DD/MM/YYYY")
          : "--",
      },
    ],
  });

  itemsWithMappedTotal.forEach((item, index) => {
    let nomePagina = "";

    if (selectedFields?.includes("unidade") && item.unidade) {
      nomePagina += `${item.unidade}`;
    }

    if (selectedFields?.includes("responsavel") && item.responsavel) {
      nomePagina += ` - ${item.responsavel}`;
    }

    if (!nomePagina) {
      nomePagina = (index + 1).toString();
    }

    sheets.push({
      sheet: sanitizeSheetName(nomePagina.slice(0, 31)),
      columns: columns,
      content: item.contas.map(defaultContentMapperXlsx),
    });
  });

  xlsx(sheets, settings);
}

function getColumns(selectedFields?: RelatorioInadimplentesFields[]) {
  const columns: IColumn[] = [];

  if (selectedFields?.includes("composicoes")) {
    columns.push({
      label: "Composições",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).composicoes
          ?.map((composicao) => `${composicao.breadcrumb} ${composicao.nome}`)
          .join(";\r\n") ?? "",
    });
  }

  if (selectedFields?.includes("descricao")) {
    columns.push({
      label: "Descrição",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).descricao ?? "",
    });
  }

  if (selectedFields?.includes("dataVencimento")) {
    columns.push({
      label: "Vencimento",
      value: (row: IContent) =>
        moment(
          (row as unknown as ContasRelatorioInadimplentes).dataVencimento,
        ).format("DD/MM/YYYY"),
    });
  }

  if (selectedFields?.includes("dataCompetencia")) {
    columns.push({
      label: "Competência",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).dataCompetencia
          ? moment(
              (row as unknown as ContasRelatorioInadimplentes).dataCompetencia,
            ).format("MM/YYYY")
          : "--",
    });
  }

  if (selectedFields?.includes("nossoNumero")) {
    columns.push({
      label: "Nosso Número",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).nossoNumero ?? "",
    });
  }

  if (selectedFields?.includes("principal")) {
    columns.push({
      label: "Principal",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).principal,
    });
  }

  if (selectedFields?.includes("multa")) {
    columns.push({
      label: "Multa",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).multa,
    });
  }

  if (selectedFields?.includes("juros")) {
    columns.push({
      label: "Juros",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).juros,
    });
  }

  if (selectedFields?.includes("valorTaxaCobranca")) {
    columns.push({
      label: "Tx. Cobrança",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).valorTaxaCobranca,
    });
  }

  if (selectedFields?.includes("valorCorrecaoMonetaria")) {
    columns.push({
      label: "Correção monetária",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).valorCorrecaoMonetaria,
    });
  }

  // columns.push({
  //   label: "Honorários",
  //   format: "#,###0.00",
  //   value: (row: IContent) =>
  //     (row as unknown as ContasRelatorioInadimplentes).honorarios,
  // });

  if (selectedFields?.includes("diasAtraso")) {
    columns.push({
      label: "Dias atraso",
      format: "#",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).diasAtraso,
    });
  }

  if (selectedFields?.includes("total")) {
    columns.push({
      label: "Total",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioInadimplentes).total,
    });
  }

  return columns;
}

function getColumnsTotais(selectedFields?: RelatorioInadimplentesFields[]) {
  const columns: IColumn[] = [];

  if (selectedFields?.includes("dataEmissao")) {
    columns.push({
      label: "Data de emissão",
      value: () => moment().format("DD/MM/YYYY"),
    });
  }

  if (selectedFields?.includes("dataPosicao")) {
    columns.push({
      label: "Data da posição",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesResponse).dataPosicao
          ? (row as unknown as RelatorioInadimplentesResponse).dataPosicao!
          : "--",
    });
  }

  columns.push({
    label: "Número de inadimplentes",
    format: "#",
    value: (row: IContent) =>
      (row as unknown as RelatorioInadimplentesResponse).numeroInadimplentes,
  });

  columns.push({
    label: "% de inadimplentes",
    format: "#,###0.00",
    value: (row: IContent) =>
      (
        100 *
        ((row as unknown as RelatorioInadimplentesResponse)
          .numeroInadimplentes /
          (row as unknown as RelatorioInadimplentesResponse).totalUnidades)
      ).toFixed(1),
  });

  if (selectedFields?.includes("principal")) {
    columns.push({
      label: "Principal",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesResponse).totalPrincipal,
    });
  }

  if (selectedFields?.includes("multa")) {
    columns.push({
      label: "Multa",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesResponse).totalMulta,
    });
  }

  if (selectedFields?.includes("juros")) {
    columns.push({
      label: "Juros",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesResponse).totalJuros,
    });
  }

  if (selectedFields?.includes("valorTaxaCobranca")) {
    columns.push({
      label: "Tx. Cobrança",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesResponse).totalTaxaCobranca,
    });
  }

  if (selectedFields?.includes("valorCorrecaoMonetaria")) {
    columns.push({
      label: "Correção monetária",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesResponse)
          .totalCorrecaoMonetaria,
    });
  }

  if (selectedFields?.includes("total")) {
    columns.push({
      label: "Total",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesResponse).total,
    });
  }

  return columns;
}

function getColumnsInadimplentes(
  selectedFields?: RelatorioInadimplentesFields[],
) {
  const columns: IColumn[] = [];

  if (selectedFields?.includes("unidade")) {
    columns.push({
      label: "Unidade",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).unidade,
    });
  }

  if (selectedFields?.includes("responsavel")) {
    columns.push({
      label: "Proprietário",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).responsavel,
    });
  }

  if (selectedFields?.includes("unidade")) {
    columns.push({
      label: "Juridico",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).isJuridico
          ? "Sim"
          : "Não",
    });
  }

  if (selectedFields?.includes("unidade")) {
    columns.push({
      label: "Ajuizada",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).isAjuizada
          ? "Sim"
          : "Não",
    });
  }

  if (selectedFields?.includes("principal")) {
    columns.push({
      label: "Principal",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).totalPrincipal,
    });
  }

  if (selectedFields?.includes("multa")) {
    columns.push({
      label: "Multa",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).totalMulta,
    });
  }

  if (selectedFields?.includes("juros")) {
    columns.push({
      label: "Juros",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).totalJuros,
    });
  }

  if (selectedFields?.includes("valorTaxaCobranca")) {
    columns.push({
      label: "Tx. Cobrança",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).totalTaxaCobranca,
    });
  }

  if (selectedFields?.includes("valorCorrecaoMonetaria")) {
    columns.push({
      label: "Correção Monetária",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).totalCorrecaoMonetaria,
    });
  }

  if (selectedFields?.includes("total")) {
    columns.push({
      label: "Total",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as RelatorioInadimplentesItem).total,
    });
  }

  return columns;
}

function getColumnsResumo() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Categoria",
    value: (row: IContent) => (row as unknown as ResumoCategoriaItem).nome,
  });

  columns.push({
    label: "Total",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as ResumoCategoriaItem).valor,
  });

  return columns;
}
